import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { imageUrl } from '../api/endpoints';

type FieldSelectorDialogProps = {
  product: {
    barcode: string;
    product_id: number;
    brand?: string;
    name?: string;
    image_url?: string;
  };
  onSelect: (field: 'a' | 'b') => void;
  onCancel: () => void;
  fieldALabel?: string;
  fieldBLabel?: string;
};

const EASE = [0.22, 0.61, 0.36, 1] as [number, number, number, number];

export default function FieldSelectorDialog({
  product,
  onSelect,
  onCancel,
  fieldALabel = 'First product',
  fieldBLabel = 'Second product',
}: FieldSelectorDialogProps) {
  const displayName = product.name ?? 'Unknown Product';
  const displayBrand = product.brand ?? 'Unknown Brand';
  const imageSrc = product.image_url ? imageUrl(product.image_url) : null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22, ease: EASE }}
        className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4"
        aria-modal="true"
        role="dialog"
        onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 backdrop-blur-md"
          style={{ background: 'var(--c-charcoal-60)' }}
        />

        {/* Sheet */}
        <motion.div
          initial={{ y: 40, scale: 0.96, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 20, scale: 0.97, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.8 }}
          className="relative w-full max-w-sm overflow-hidden"
          style={{
            background: 'var(--c-cream)',
            borderRadius: 'var(--r-xl)',
            boxShadow: 'var(--sh-deep)',
          }}
          onClick={(e) => { e.stopPropagation(); }}
        >
          {/* Header */}
          <div className="px-6 pt-7 pb-5">
            <p className="t-label mb-2">
              Product detected
            </p>
            <h3
              className="t-h3 text-[var(--ink)]"
              style={{ letterSpacing: '-0.01em' }}
            >
              Which slot to fill?
            </h3>
          </div>

          <div className="h-px mx-6" style={{ background: 'var(--rule)' }} />

          {/* Product preview */}
          <div className="px-6 py-5">
            <div
              className="flex items-center gap-4 p-3"
              style={{ background: 'var(--c-sand)', borderRadius: 'var(--r-md)' }}
            >
              {imageSrc ? (
                <motion.img
                  initial={{ scale: 0.86, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.3, ease: EASE }}
                  src={imageSrc}
                  alt={displayName}
                  className="w-16 h-16 flex-shrink-0 object-contain"
                  style={{ background: 'var(--c-cream)', borderRadius: 'var(--r-sm)' }}
                />
              ) : (
                <div
                  className="w-16 h-16 flex-shrink-0 grid place-items-center"
                  style={{ background: 'var(--c-teal-weak)', borderRadius: 'var(--r-sm)' }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                    stroke="var(--c-kivo-teal)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="3" />
                    <path d="M8.5 12h7M12 8.5v7" />
                  </svg>
                </div>
              )}

              <div className="flex-1 min-w-0">
                <motion.p
                  initial={{ y: 4, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.14, duration: 0.26, ease: EASE }}
                  className="t-label truncate mb-1"
                >
                  {displayBrand}
                </motion.p>
                <motion.p
                  initial={{ y: 4, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.18, duration: 0.26, ease: EASE }}
                  className="font-display text-[17px] leading-tight truncate"
                  style={{ color: 'var(--ink)', letterSpacing: '-0.01em' }}
                >
                  {displayName}
                </motion.p>
                <motion.p
                  initial={{ y: 4, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.22, duration: 0.26, ease: EASE }}
                  className="text-[11px] mt-0.5 font-mono"
                  style={{ color: 'var(--c-charcoal-30)' }}
                >
                  {product.barcode}
                </motion.p>
              </div>
            </div>
          </div>

          {/* Field buttons */}
          <div className="px-6 pb-5 space-y-2.5">
            <button
              type="button"
              onClick={() => { onSelect('a'); }}
              className="btn btn--field-a w-full flex items-center justify-between px-5 py-3.5 rounded-full text-sm font-medium"
            >
              <span>{fieldALabel}</span>
              <span
                className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(248,244,238,0.2)', letterSpacing: '0.1em' }}
              >
                A
              </span>
            </button>

            <button
              type="button"
              onClick={() => { onSelect('b'); }}
              className="btn btn--field-b w-full flex items-center justify-between px-5 py-3.5 rounded-full text-sm font-medium"
            >
              <span>{fieldBLabel}</span>
              <span
                className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: 'var(--c-teal-weak)', letterSpacing: '0.1em' }}
              >
                B
              </span>
            </button>
          </div>

          {/* Cancel */}
          <div className="px-6 pb-6">
            <div className="h-px mb-5" style={{ background: 'var(--rule)' }} />
            <button
              type="button"
              onClick={onCancel}
              className="btn btn--cancel w-full py-2.5 rounded-full text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
